import React from 'react';
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { 
  MessageCircle, 
  ChevronRight, 
  Clock, 
  MapPin, 
  Mail, 
  UtensilsCrossed,
  ArrowRight
} from 'lucide-react';
import { SiWhatsapp, SiInstagram } from 'react-icons/si';
import { ROUTE_PATHS, RESTAURANT_CONSTANTS } from '@/lib/index';
import { restaurantInfo, menuData } from '@/data/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const Home: React.FC = () => {
  const featuredPlats = menuData.find(cat => cat.id === 'plats-signatures')?.items.slice(0, 3) || [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.AFRICAN_CUISINE_1} 
            alt="Cuisine Africaine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="container relative z-10 px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              {RESTAURANT_CONSTANTS.NAME}
            </h1>
            <p className="text-xl md:text-2xl mb-8 font-light max-w-2xl mx-auto text-white/90">
              Cuisine Africaine & Européenne Maison
              <br />
              <span className="italic">Commande rapide et plats faits maison</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                asChild 
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-full px-8 py-7 text-lg shadow-xl hover:scale-105 transition-all"
              >
                <a href={RESTAURANT_CONSTANTS.WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                  <SiWhatsapp className="mr-2 h-6 w-6" />
                  Commander sur WhatsApp
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                asChild
                className="bg-white/10 backdrop-blur-md border-white/20 text-white hover:bg-white/20 rounded-full px-8 py-7 text-lg"
              >
                <NavLink to={ROUTE_PATHS.MENU}>
                  Découvrir le Menu
                  <ChevronRight className="ml-2 h-5 w-5" />
                </NavLink>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features/Intro Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              whileHover={{ y: -5 }}
              className="p-8 rounded-2xl bg-secondary/50 border border-border flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 text-primary">
                <UtensilsCrossed className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Plats Authentiques</h3>
              <p className="text-muted-foreground">
                Une fusion unique de saveurs africaines traditionnelles et de classiques européens.
              </p>
            </motion.div>

            <motion.div 
              whileHover={{ y: -5 }}
              className="p-8 rounded-2xl bg-secondary/50 border border-border flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 text-primary">
                <Clock className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Rapidité & Fraîcheur</h3>
              <p className="text-muted-foreground">
                Tous nos plats sont préparés à la commande avec des produits frais du jour.
              </p>
            </motion.div>

            <motion.div 
              whileHover={{ y: -5 }}
              className="p-8 rounded-2xl bg-secondary/50 border border-border flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 text-primary">
                <MessageCircle className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Service WhatsApp</h3>
              <p className="text-muted-foreground">
                Commandez en un clic. Simple, rapide et direct vers notre cuisine.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Menu Teaser */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Nos Plats Signatures</h2>
              <p className="text-muted-foreground text-lg max-w-xl">
                Découvrez une sélection de nos meilleures créations culinaires, préparées avec passion par Karine.
              </p>
            </div>
            <Button variant="link" asChild className="text-primary text-lg p-0">
              <NavLink to={ROUTE_PATHS.MENU} className="flex items-center">
                Voir tout le menu <ArrowRight className="ml-2 h-5 w-5" />
              </NavLink>
            </Button>
          </div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {featuredPlats.map((item) => (
              <motion.div key={item.id} variants={itemVariants}>
                <Card className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-shadow bg-card h-full flex flex-col">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={item.image || IMAGES.AFRICAN_CUISINE_2} 
                      alt={item.name} 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-bold text-xl leading-tight">{item.name}</h3>
                      <span className="font-mono font-bold text-primary text-lg">{item.price}€</span>
                    </div>
                    <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                      {item.description}
                    </p>
                    <div className="mt-auto pt-4">
                      <Button 
                        size="sm" 
                        className="w-full bg-secondary text-secondary-foreground hover:bg-primary hover:text-white transition-colors"
                        asChild
                      >
                        <a href={RESTAURANT_CONSTANTS.WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                          Commander
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={IMAGES.FRESH_INGREDIENTS_1} 
                  alt="Ingrédients frais"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-accent rounded-2xl -z-10 hidden md:block" />
              <div className="absolute -top-6 -left-6 w-32 h-32 border-4 border-primary rounded-2xl -z-10 hidden md:block" />
            </div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h4 className="text-accent font-bold uppercase tracking-widest text-sm mb-4">Notre Histoire</h4>
              <h2 className="text-4xl font-bold mb-6 text-primary">L'Art de Recevoir par Mabaga Karine</h2>
              <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
                <p>{restaurantInfo.description}</p>
                <p>
                  Chaque plat est une invitation au voyage, un mélange harmonieux entre les épices vibrantes d'Afrique 
                  et le raffinement des techniques européennes. Notre cuisine est avant tout une cuisine de cœur, 
                  destinée à être partagée.
                </p>
              </div>
              <Button asChild className="mt-8 bg-primary hover:bg-primary/90 rounded-full px-8 py-6">
                <NavLink to={ROUTE_PATHS.ABOUT}>En savoir plus sur nous</NavLink>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Galerie de nos plats */}
      <section className="py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Nos Créations Culinaires</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Découvrez en images la beauté et l'authenticité de nos plats préparés avec amour
            </p>
          </div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {[
              { image: IMAGES.CAPTURE_D_CRAN2854, alt: "Spécialité africaine authentique" },
              { image: IMAGES.CAPTURE_D_CRAN6495, alt: "Plat signature du chef" },
              { image: IMAGES.CAPTURE_D_CRAN8415, alt: "Cuisine européenne raffinée" },
              { image: IMAGES.CAPTURE_D_CRAN2274, alt: "Mélange de saveurs africaines et européennes" },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className="aspect-square rounded-xl overflow-hidden shadow-lg cursor-pointer group"
              >
                <img 
                  src={item.image} 
                  alt={item.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </motion.div>
          
          <div className="text-center mt-12">
            <Button asChild variant="outline" className="rounded-full px-8 py-6 text-lg border-primary text-primary hover:bg-primary hover:text-white">
              <NavLink to={ROUTE_PATHS.INSTAGRAM}>Voir plus sur Instagram</NavLink>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Quick Info */}
      <section className="bg-primary text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="flex flex-col items-center md:items-start text-center md:text-left">
              <div className="flex items-center mb-4">
                <MapPin className="mr-3 h-6 w-6 text-accent" />
                <h4 className="text-lg font-bold">Adresse</h4>
              </div>
              <p className="text-white/80">{RESTAURANT_CONSTANTS.ADDRESS}</p>
            </div>

            <div className="flex flex-col items-center md:items-start text-center md:text-left">
              <div className="flex items-center mb-4">
                <Clock className="mr-3 h-6 w-6 text-accent" />
                <h4 className="text-lg font-bold">Horaires</h4>
              </div>
              <p className="text-white/80">{RESTAURANT_CONSTANTS.HOURS}</p>
            </div>

            <div className="flex flex-col items-center md:items-start text-center md:text-left">
              <div className="flex items-center mb-4">
                <Mail className="mr-3 h-6 w-6 text-accent" />
                <h4 className="text-lg font-bold">Contact</h4>
              </div>
              <p className="text-white/80">{RESTAURANT_CONSTANTS.EMAIL}</p>
            </div>

            <div className="flex flex-col items-center md:items-end justify-center">
              <div className="flex gap-4">
                <Button 
                  size="icon" 
                  variant="outline" 
                  className="rounded-full bg-white/10 border-white/20 hover:bg-accent hover:text-accent-foreground"
                  asChild
                >
                  <a href={RESTAURANT_CONSTANTS.WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                    <SiWhatsapp className="h-5 w-5" />
                  </a>
                </Button>
                <Button 
                  size="icon" 
                  variant="outline" 
                  className="rounded-full bg-white/10 border-white/20 hover:bg-accent hover:text-accent-foreground"
                  asChild
                >
                  <a href={RESTAURANT_CONSTANTS.INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer">
                    <SiInstagram className="h-5 w-5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;